import axios from "axios";

const MAX_RETRY_AFTER_MS = 30_000;
const RETRY_BASE_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 10_000;

type DatabaseErrorPayload = {
  code?: unknown;
  reason?: unknown;
};

function databaseReason(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const payload = error.response?.data as DatabaseErrorPayload | undefined;
  return payload?.code === "database_unavailable" && typeof payload.reason === "string"
    ? payload.reason
    : null;
}

/**
 * Retry only failures which are normally transient. Query/lock timeouts are
 * deliberately left to the visible retry button: immediately repeating the
 * same expensive query would amplify pressure on an already busy database.
 */
export function isTransientQueryError(error: unknown): boolean {
  if (axios.isCancel(error)) return false;
  if (!axios.isAxiosError(error)) return false;
  if (error.config?._readRecoveryExhausted) return false;

  const status = error.response?.status;
  if (status == null || status === 429 || status === 502 || status === 504) {
    return true;
  }
  if (status !== 503) return false;

  return !["statement_timeout", "lock_timeout"].includes(databaseReason(error) ?? "");
}

/**
 * Pointer reads are tiny and idempotent. Unlike expensive data queries, they
 * may safely retry a transient lock/statement timeout during an atomic release.
 */
export function isTransientPointerError(error: unknown): boolean {
  if (axios.isAxiosError(error) && error.config?._readRecoveryExhausted) return false;
  if (isTransientQueryError(error)) return true;
  if (!axios.isAxiosError(error) || error.response?.status !== 503) return false;
  return ["statement_timeout", "lock_timeout"].includes(databaseReason(error) ?? "");
}

/**
 * Availability classification, not permission to retry. A completed retry
 * budget does not turn a transport outage into an authorization revocation.
 * Used only to preserve already-authorized anonymous demo content.
 */
export function isDemoPointerAvailabilityFailure(error: unknown): boolean {
  if (axios.isCancel(error) || !axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status == null || [429, 502, 503, 504].includes(status);
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // The API already exhausted its bounded recovery; avoid multiplying retries.
  if (axios.isAxiosError(error) && error.config?._readRecoveryExhausted) return false;
  return failureCount < 2 && isTransientQueryError(error);
}

/** Parses a server retry hint without allowing an unbounded client-side stall. */
export function retryAfterDelayMs(value: unknown, now = Date.now()): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string") return null;
  const seconds = Number(raw);
  const delay = Number.isFinite(seconds) && seconds >= 0
    ? seconds * 1000
    : Date.parse(raw) - now;
  return Number.isFinite(delay) && delay >= 0
    ? Math.min(delay, MAX_RETRY_AFTER_MS)
    : null;
}

/**
 * Exponential backoff plus per-client jitter prevents synchronized retry
 * waves. A Retry-After value remains the minimum wait unless it reaches the
 * hard safety cap.
 */
export function queryRetryDelay(
  attempt: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  const headers = axios.isAxiosError(error) ? error.response?.headers : undefined;
  const normalizedHeaders = headers as unknown as {
    get?: (name: string) => string | null | undefined;
    ["retry-after"]?: unknown;
  } | undefined;
  const retryAfter = retryAfterDelayMs(
    typeof normalizedHeaders?.get === "function"
      ? normalizedHeaders.get("retry-after")
      : normalizedHeaders?.["retry-after"],
  );
  if (retryAfter != null) {
    const jitter = Math.round(Math.min(2_000, retryAfter * 0.2) * random());
    return Math.min(MAX_RETRY_AFTER_MS, retryAfter + jitter);
  }
  const exponential = Math.min(MAX_RETRY_DELAY_MS, RETRY_BASE_DELAY_MS * 2 ** attempt);
  return Math.round(exponential * (0.5 + random()));
}

/** Shared by Overview and tested through an actual QueryClient fetch. */
export const readQueryRecoveryOptions = {
  retry: shouldRetryQuery,
  retryDelay: queryRetryDelay,
};